import { createSelector } from "@reduxjs/toolkit";
import { IIssueApi } from "@schemas/issueSchema";
import { Edge, MarkerType, Node } from "@xyflow/react";
import { selectFilteredIssuesOnBoard, selectIssueBoard } from "../../issueSelectors";

import { INoteboardImageNode } from "@mcapps/shared";
import { selectHttpIssuesOnBoard } from "../../issueSelectors";

// ---- datatyper för noderna
type IssueNodeData = { issue: IIssueApi };
type FlowNode = Node<IssueNodeData | ImageNodeData>;

/** Mappa IIssueApi -> ReactFlow Node */
type NodeData = { issue: IIssueApi };

// ⚠️ Ha delade tom-konstanter för att inte skapa nya referenser
const EMPTY_ISSUES: IIssueApi[] = [];

export const selectIssuesWithoutNoteboardNode = createSelector([selectFilteredIssuesOnBoard], (issues) => {
  if (issues == null) return [];
  return issues.filter((issue) => issue.noteboardNode?.visible !== true);
});

export const selectIssuesWithNode = createSelector([selectFilteredIssuesOnBoard], (issues) => {
  if (issues == null) return [];
  return issues.filter((issue) => issue.noteboardNode?.visible === true);
});

/**
 * Mappa connections -> ReactFlow Edge[]
 * OBS: justera fältet nedan efter hur dina kopplingar faktiskt ser ut i IIssueApi.
 * Nedan exempel förväntar sig en lista med issueIds på issue.connections.common.to
 */
export const selectFlowEdges = createSelector([selectIssuesWithNode], (issues: IIssueApi[]): Edge[] => {
  const visibleIds = new Set(issues.map((i) => i._id));
  const seen = new Set<string>();
  const edges: Edge[] = [];

  for (const child of issues) {
    const parents = child.connections?.common?.parents ?? [];

    for (const parentId of parents) {
      if (!visibleIds.has(parentId)) continue; // bara mellan synliga noder

      const key = `${child._id}->${parentId}`; // riktad kant (child -> parent)
      if (seen.has(key)) continue; // skydd mot dubbletter om datat skulle råka innehålla det
      seen.add(key);

      edges.push({
        id: key,
        source: child._id,
        target: parentId,
        markerEnd: { type: MarkerType.ArrowClosed, width: 24, height: 24, color: "#5b5b5bff" },
        // matcha dina handles i KanbanIssueNode:
        sourceHandle: "bottom",
        targetHandle: "top",
      });
    }
  }

  return edges;
});

const EMPTY_IMAGES: readonly INoteboardImageNode[] = [];

// 2) Bild-data från boardet
export const selectImageNodesRaw = createSelector([selectIssueBoard], (board) =>
  // gör en MUTERBAR kopia (RF kommer att uppdatera width/height mm)
  (board?.noteboardImageNodes ?? []).map((n) => ({ ...n })),
);

// RF-data för bild-noden (enkelt: bara path)
export type ImageNodeData = { path: string };
// 3) Bild-noder -> RF nodes
export const selectFlowImageNodes = createSelector([selectImageNodesRaw], (imgs): FlowNode[] =>
  imgs.map((img) => ({
    id: String(img._id),
    type: "imageNode",
    position: { x: img.x ?? 0, y: img.y ?? 0 },
    data: { path: `/api/image/${img.path}` },
    width: img.width ?? 900,
    height: img.height ?? 600,
    style: { zIndex: 0 },
  })),
);

// 1) Issue-noder -> RF nodes
export const selectFlowIssueNodes = createSelector([selectIssuesWithNode], (issues): FlowNode[] =>
  issues.map((issue) => ({
    id: issue._id,
    type: "issueNode",
    position: {
      x: issue.noteboardNode?.x ?? 0,
      y: issue.noteboardNode?.y ?? 0,
    },
    data: { issue },
    style: { zIndex: 100 },
  })),
);

export const selectFlowNodes = createSelector(
  [selectFlowIssueNodes, selectFlowImageNodes],
  (issueNodes, imageNodes): FlowNode[] => [...issueNodes, ...imageNodes],
);
